'use client';

import { useRef, useState, type RefObject } from 'react';
import { ChatBubbleLeftRightIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useResponsivePaneDialog } from './useResponsivePaneDialog';

export type AssistantConversationSummary = {
  id: string;
  title: string;
  updatedAt: string;
};

function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recent';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
}

function conversationTitle(value: string): string {
  const title = value.trim();
  if (!title || title === 'New chat' || title === 'Assistant v2 lab') return 'New conversation';
  return title;
}

function HistoryContents({ conversations, activeId, onSelect, onNew, onClose, closeButtonRef, headingId }: { conversations: AssistantConversationSummary[]; activeId?: string; onSelect: (id: string) => void; onNew: () => void; onClose?: () => void; closeButtonRef?: RefObject<HTMLButtonElement | null>; headingId?: string }) {
  const [query, setQuery] = useState('');
  // An untitled thread that was never used is an artifact of opening the assistant,
  // not a conversation the student had. Hide those unless one is the open thread,
  // so history reads as work done rather than a list of empty starts.
  const meaningful = conversations.filter((conversation) => conversation.id === activeId || conversationTitle(conversation.title) !== 'New conversation');
  const visibleConversations = meaningful.filter((conversation) => conversationTitle(conversation.title).toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <>
      <header className="flex items-center justify-between gap-2 border-b border-[var(--ss-border)] px-3 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <ChatBubbleLeftRightIcon className="size-5 shrink-0 text-[var(--ss-text-muted)]" aria-hidden="true" />
          <h2 id={headingId} className="truncate text-sm font-semibold text-[var(--ss-text)]">Conversations</h2>
        </div>
        {onClose ? <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close conversation history" className="grid size-12 place-items-center rounded-control text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)]"><XMarkIcon className="size-5" aria-hidden="true" /></button> : null}
      </header>
      <div className="p-3">
        <button type="button" onClick={onNew} className="flex min-h-12 w-full items-center justify-center gap-2 rounded-control bg-[var(--ss-brand)] px-3 text-sm font-semibold text-white hover:bg-[var(--ss-brand-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">
          <PlusIcon className="size-5" aria-hidden="true" />
          New conversation
        </button>
        <label className="mt-2 flex min-h-11 items-center gap-2 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)] px-3">
          <MagnifyingGlassIcon className="size-4 shrink-0 text-[var(--ss-text-muted)]" aria-hidden="true" />
          <span className="sr-only">Search conversations</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search conversations" className="min-w-0 flex-1 bg-transparent text-sm text-[var(--ss-text)] outline-none placeholder:text-[var(--ss-text-muted)]" />
        </label>
      </div>
      <nav aria-label="Conversation history" className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {visibleConversations.length ? (
          <ul className="space-y-1">
            {visibleConversations.map((conversation) => {
              const active = conversation.id === activeId;
              return (
                <li key={conversation.id}>
                  <button type="button" onClick={() => onSelect(conversation.id)} aria-current={active ? 'page' : undefined} className={`w-full rounded-card border-l-2 px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${active ? 'border-l-[var(--ss-brand)] bg-[var(--ss-surface-subtle)] text-[var(--ss-text)]' : 'border-l-transparent text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)]'}`}>
                    <span className="block truncate text-sm font-medium">{conversationTitle(conversation.title)}</span>
                    <span className="mt-1 block text-xs text-[var(--ss-text-muted)]">{formatUpdatedAt(conversation.updatedAt)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : <p className="px-3 py-6 text-sm leading-6 text-[var(--ss-text-muted)]">{conversations.length ? 'No conversations match your search.' : 'Your conversations will appear here.'}</p>}
      </nav>
    </>
  );
}

export function ConversationHistory({ conversations, activeId, onSelect, onNew, mobileOpen, onMobileClose, overlayOnly = false }: { conversations: AssistantConversationSummary[]; activeId?: string; onSelect: (id: string) => void; onNew: () => void; mobileOpen: boolean; onMobileClose: () => void; /** Surfaces with no room for a persistent rail (the compact modal) show only the overlay. */ overlayOnly?: boolean }) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { paneRef, onKeyDown } = useResponsivePaneDialog(onMobileClose, closeButtonRef, mobileOpen);
  return (
    <>
      {overlayOnly ? null : (
        <aside className="hidden min-h-0 w-60 shrink-0 flex-col border-r border-[var(--ss-border)] bg-[var(--ss-surface)] md:flex">
          <HistoryContents conversations={conversations} activeId={activeId} onSelect={onSelect} onNew={onNew} />
        </aside>
      )}
      {mobileOpen ? (
        <div className={`absolute inset-0 z-30 ${overlayOnly ? '' : 'md:hidden'}`}>
          <button type="button" aria-label="Close conversation history backdrop" onClick={onMobileClose} className="absolute inset-0 bg-slate-950/35 backdrop-blur-[2px]" />
          <aside ref={paneRef} role="dialog" aria-modal="true" aria-label="Conversation history" onKeyDown={onKeyDown} className="absolute inset-y-0 left-0 flex w-[min(20rem,88vw)] flex-col border-r border-[var(--ss-border)] bg-[var(--ss-surface)] shadow-e3">
            <HistoryContents conversations={conversations} activeId={activeId} onSelect={onSelect} onNew={onNew} onClose={onMobileClose} closeButtonRef={closeButtonRef} headingId="assistant-conversation-history-title" />
          </aside>
        </div>
      ) : null}
    </>
  );
}
